import { useCallback, useEffect, useState } from "react";
import { get, post } from "../../../redux/ApiHelper";
import { Column } from "../../../interfaces/table.interface";
import TableComponent from "../../../components/table/TableComponent";
import { formatDateTime } from "../../../utils/date&time.util";
import {
  AdminPaymentTransaction,
  PaymentItemTypeOption,
} from "../../../interfaces/payment.interface";
import { IconButton, Tooltip } from "@mui/material";
import { FaCopy } from "react-icons/fa";
import { errorToast, successToast } from "../../../utils/toast.util";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import {
  buildAdminVerifyBody,
  canAdminVerifyTransaction,
  getVerifyOrderId,
} from "../../../utils/adminPaymentVerify.util";

const PaymentsPage = () => {
  const [paymentsList, setPaymentsList] = useState<AdminPaymentTransaction[]>(
    []
  );
  const [filteredPaymentsList, setFilteredPaymentsList] = useState<
    AdminPaymentTransaction[]
  >([]);
  const [itemTypeOptions, setItemTypeOptions] = useState<
    PaymentItemTypeOption[]
  >([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [itemTypeFilter, setItemTypeFilter] = useState<string>("");
  const [verifyingMap, setVerifyingMap] = useState<Record<string, boolean>>({});
  const { signedAdmin } = useSelector((state: RootState) => state.admin);
  const isFranchiseAdmin = signedAdmin.type === "franchise";

  const applySearchFilter = useCallback(
    (list: AdminPaymentTransaction[], query: string) => {
      const lowerQuery = query.toLowerCase();
      if (!lowerQuery) return list;

      return list.filter((payment) => {
        const createdByName = payment.createdBy?.name?.toLowerCase() || "";
        const role = payment.role?.toLowerCase() || "";
        const transactionId = payment.transactionId?.toLowerCase() || "";
        const externalTransactionId =
          payment.externalTransactionId?.toLowerCase() || "";
        const paymentDate = formatDateTime(payment?.createdAt);
        const productLabel = payment.productLabel?.toLowerCase() || "";
        const itemType = payment.itemType?.toLowerCase() || "";

        return (
          payment._id.includes(lowerQuery) ||
          payment.amount.toString().includes(lowerQuery) ||
          payment.currency.toLowerCase().includes(lowerQuery) ||
          role.includes(lowerQuery) ||
          createdByName.includes(lowerQuery) ||
          transactionId.includes(lowerQuery) ||
          paymentDate.toLowerCase().includes(lowerQuery) ||
          externalTransactionId.includes(lowerQuery) ||
          productLabel.includes(lowerQuery) ||
          itemType.includes(lowerQuery)
        );
      });
    },
    []
  );

  const fetchPayments = useCallback(async () => {
    const queryParams: Record<string, string> = {
      type: String(signedAdmin.type),
      adminId: String(signedAdmin.id),
    };

    if (roleFilter) {
      queryParams.role = roleFilter;
    }
    if (itemTypeFilter) {
      queryParams.itemType = itemTypeFilter;
    }

    try {
      const res = await get("/admin/payment/transactions", queryParams);
      const transactions = res.transactions ?? [];
      setPaymentsList(transactions);
      setItemTypeOptions(res.itemTypeOptions ?? []);
    } catch {
      errorToast("Failed to fetch payments");
    }
  }, [signedAdmin.id, signedAdmin.type, roleFilter, itemTypeFilter]);

  useEffect(() => {
    void fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    setFilteredPaymentsList(applySearchFilter(paymentsList, searchQuery));
  }, [paymentsList, searchQuery, applySearchFilter]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const visibleItemTypeOptions = itemTypeOptions.filter((option) => {
    if (!roleFilter) return true;
    return option.roles?.includes(roleFilter as "Parent" | "Teacher") ?? true;
  });

  const handleRoleFilterChange = (value: string) => {
    setRoleFilter(value);
    if (
      itemTypeFilter &&
      value &&
      !itemTypeOptions.find(
        (option) =>
          option.value === itemTypeFilter &&
          option.roles?.includes(value as "Parent" | "Teacher")
      )
    ) {
      setItemTypeFilter("");
    }
  };

  const columns: Column[] = [
    { select: "_id", header: "Payment ID", align: "left" },
    { select: "role", header: "Role" },
    {
      header: "Payment Type",
      render: (_value: unknown, row: AdminPaymentTransaction) =>
        row.productLabel || row.itemType || "N/A",
    },
    {
      header: "Created By",
      render: (_value: unknown, row: AdminPaymentTransaction) =>
        row?.createdBy?.name || "N/A",
    },
    {
      header: "amount",
      render: (_value: unknown, row: AdminPaymentTransaction) => {
        const collectedAmount = row.amount;
        const gatewayCharge = (collectedAmount * 2) / 100;
        const platformFee = (collectedAmount * 20) / 100;
        const gstOnPlatformFee = (platformFee * 18) / 100;
        const amountToBeTransferred =
          collectedAmount - gstOnPlatformFee - gatewayCharge;

        const formatINR = (amount: number) =>
          amount.toLocaleString("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });

        return (
          <div className="space-y-1">
            {signedAdmin.type === "franchise" ? (
              <div>
                <p className="whitespace-nowrap">
                  Collected amount: {formatINR(collectedAmount)}
                </p>
                <p className="whitespace-nowrap">
                  Gateway Charge (2%): {formatINR(gatewayCharge)}
                </p>
                <p className="whitespace-nowrap">
                  Platform Fee (20%): {formatINR(platformFee)}
                </p>
                <p className="whitespace-nowrap">
                  GST on Platform Fee (18%): {formatINR(gstOnPlatformFee)}
                </p>
                <p className="whitespace-nowrap font-semibold text-green-600">
                  Amount to be transferred: {formatINR(amountToBeTransferred)}
                </p>
              </div>
            ) : signedAdmin.type === "employee" ? (
              <div>{collectedAmount}</div>
            ) : null}
          </div>
        );
      },
    },
    { select: "currency", header: "Currency" },
    {
      header: "Status",
      render: (_value: unknown, row: AdminPaymentTransaction) => {
        const isCompleted = row?.status === "Completed";

        return (
          <div className="flex items-center">
            <span
              className={`status-badge ${
                isCompleted
                  ? "bg-green-500"
                  : row?.status === "Pending"
                    ? "bg-yellow-500"
                    : "bg-red-500"
              } text-white px-2 py-1 rounded`}
            >
              {row?.status}
            </span>
          </div>
        );
      },
    },
    {
      header: "Payment Status",
      render: (_value: unknown, row: AdminPaymentTransaction) =>
        row?.paymentStatus || "N/A",
    },
    {
      header: "Action",
      render: (_value: unknown, row: AdminPaymentTransaction) => {
        const isVerifying = verifyingMap[row._id];
        const canVerify = canAdminVerifyTransaction(row);

        const handleVerify = async () => {
          setVerifyingMap((prev) => ({ ...prev, [row._id]: true }));
          const orderId = getVerifyOrderId(row);
          post(`/admin/payment/transactions/verify/${orderId}`, buildAdminVerifyBody(row))
            .then((response) => {
              const { newPaymentData, message, success } = response;

              if (success === false) {
                errorToast(message || "Failed to verify payment. Please try again.");
                return;
              }

              const updatedList = paymentsList.map((payment) =>
                payment._id === row._id
                  ? {
                      ...payment,
                      ...newPaymentData,
                      status: "Completed" as const,
                    }
                  : payment
              );
              setPaymentsList(updatedList);
              setFilteredPaymentsList(updatedList);
              successToast(message || "Payment successfully updated!");
            })
            .catch((error: { message?: string }) =>
              errorToast(
                error?.message || "Failed to verify payment. Please try again."
              )
            )
            .finally(() =>
              setVerifyingMap((prev) => ({ ...prev, [row._id]: false }))
            );
        };

        if (!canVerify) {
          return null;
        }

        return (
          <div className="flex items-center">
            <div className="ml-2">
              <Tooltip
                title="Click to verify the payment if it seems stuck. This will validate the payment status."
                placement="top"
              >
                <button
                  className={`text-white px-3 py-1 rounded ${
                    isVerifying
                      ? "bg-blue-300 cursor-not-allowed"
                      : "bg-blue-500 hover:bg-blue-600"
                  }`}
                  onClick={handleVerify}
                  disabled={isVerifying}
                  aria-label="Verify Transaction"
                >
                  {isVerifying ? "Verifying..." : "Verify Transaction"}
                </button>
              </Tooltip>
            </div>
          </div>
        );
      },
    },
    {
      header: "Transaction ID",
      render: (_value: unknown, row: AdminPaymentTransaction) =>
        row?.transactionId || "N/A",
    },
    {
      header: "External Transaction ID",
      render: (_value: unknown, row: AdminPaymentTransaction) => {
        const transactionId = row?.externalTransactionId || "N/A";

        if (transactionId === "N/A") {
          return transactionId;
        }

        const handleCopy = () => {
          navigator.clipboard.writeText(transactionId);
          successToast("Transaction ID copied to clipboard!");
        };

        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ marginRight: 8 }}>
              {transactionId.slice(0, 10)}...
            </span>
            <Tooltip title="Copy Transaction ID">
              <IconButton
                onClick={handleCopy}
                size="small"
                aria-label="Copy Transaction ID"
              >
                <FaCopy />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    },
    {
      header: "Date & Time",
      render: (_value: unknown, row: AdminPaymentTransaction) =>
        formatDateTime(row?.createdAt),
    },
    {
      header: "amount breakup",
      render: (_value: unknown, row: AdminPaymentTransaction) => {
        const collectedAmount = row.amount;
        const gatewayCharge = (collectedAmount * 2) / 100;
        const platformFee = (collectedAmount * 20) / 100;
        const gstOnPlatformFee = (platformFee * 18) / 100;
        const amountToBeTransferred =
          collectedAmount - gstOnPlatformFee - gatewayCharge;

        const formatINR = (amount: number) =>
          amount.toLocaleString("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });

        return (
          <div className="space-y-1">
            <div>
              <p className="whitespace-nowrap">
                Collected amount: {formatINR(collectedAmount)}
              </p>
              <p className="whitespace-nowrap">
                Gateway Charge (2%): {formatINR(gatewayCharge)}
              </p>
              <p className="whitespace-nowrap">
                Platform Fee (20%): {formatINR(platformFee)}
              </p>
              <p className="whitespace-nowrap">
                GST on Platform Fee (18%): {formatINR(gstOnPlatformFee)}
              </p>
              <p className="whitespace-nowrap font-semibold text-green-600">
                Amount to be transferred: {formatINR(amountToBeTransferred)}
              </p>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="paymentsPage table-container">
      <div className="title">Payments</div>

      <div className="mb-4 flex flex-col gap-3 md:flex-row">
        <select
          value={roleFilter}
          onChange={(e) => handleRoleFilterChange(e.target.value)}
          className="w-full md:w-48 px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700 bg-white"
        >
          <option value="">All Roles</option>
          <option value="Parent">Parent</option>
          {!isFranchiseAdmin && <option value="Teacher">Teacher</option>}
        </select>

        <select
          value={itemTypeFilter}
          onChange={(e) => setItemTypeFilter(e.target.value)}
          className="w-full md:flex-1 px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700 bg-white"
        >
          <option value="">All Payment Types</option>
          {visibleItemTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="search-container mb-4">
        <input
          type="text"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700"
          placeholder="Search payments"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      <TableComponent
        columns={columns.filter((item) => {
          if (
            signedAdmin.type === "franchise" &&
            (item.header === "Action" || item.header === "amount breakup")
          ) {
            return false;
          }
          return true;
        })}
        data={filteredPaymentsList}
      />
    </div>
  );
};

export default PaymentsPage;
